import app from 'flarum/forum/app';
import api from './api';
import { placeFromPath, windowsThatFit } from './util';
const HEARTBEAT = 30000;
const HEARTBEAT_NO_REALTIME = 15000;
const POLL_OPEN = 5000;
const PRESENCE_MIN_GAP = 10000;
const TYPING_TTL = 6000;
const TYPING_SEND_EVERY = 3000;
const MAX_HEADS = 6;
/** How long a queued message waits before trying again, growing to the last. */
const RETRY_STEPS = [2000, 4000, 8000, 15000, 30000];
/** How long after you last used a room you still count as chatting in it. */
const ROOM_ACTIVE_MS = 3 * 60 * 1000;
export const RAIL_WIDTH = 300;
export const DOCK_BREAKPOINT = 1200;
export const MOBILE_BREAKPOINT = 820;

/**
 * Everything Parley knows, for every component that draws it.
 *
 * One instance per page load, reached as `app.parley`. Kept off the module's
 * default export as an INSTANCE on purpose: Flarum's auto-export loader cannot
 * register `export default new X()`, so Parley Calls reaches this through
 * `app.parley`, not an import.
 */
export default class ParleyState {
  constructor() {
    this.config = app.forum.attribute('parley') || {};
    const user = app.session.user;
    this.me = user;
    this.chosenStatus = user && user.preferences()?.parleyStatus || 'online';
    this.idle = false;
    this.online = [];
    this.followingOffline = [];
    this.more = 0;
    this.guests = 0;
    this.loaded = false;

    /** Rooms this person can see, in the admin's order, from the heartbeat. */
    this.rooms = [];
    /** A conference's team rooms, fetched when it is opened in the rail. */
    this.childRooms = {};
    /** Which parent rooms are open in the rail; remembered between visits. */
    this.expanded = new Set();
    /** Every person met in a message, by id — a room's authors are not known up front. */
    this.cards = new Map();

    /** id → { summary, messages, hasOlder, loadingOlder, typing: {userId: until} } */
    this.convs = new Map();
    this.unread = {};
    /** Message requests waiting, and muted conversations with news: quiet counts. */
    this.requests = 0;
    this.quiet = new Set();
    /** Unsent words per conversation, kept across reloads. */
    this.drafts = {};
    /** Messages on their way: sent, retrying through a dropped connection, or refused. */
    this.queue = [];
    /** A group's members, fetched with its details, for @mentions and the details column. */
    this.members = {};
    this.open = [];
    this.heads = [];
    this.minimised = new Set();
    this.flash = new Set();
    this.replyTo = {};
    this.editing = {};
    this.reporting = null;
    /** The conversation the inbox page is showing, if it is open. */
    this.pageConversation = null;
    this.filter = '';
    this.found = [];
    this.sections = {
      rooms: true,
      follow: true,
      all: true
    };
    this.statusMenu = false;
    /**
     * The people list floats in a panel over the page, popping up from the
     * online button, unless the member pins it down the side ("docked"),
     * which makes the page narrower. `railSheet` is that panel being open;
     * on a phone it fills the screen.
     */
    this.railPinned = false;
    this.railHidden = true;
    this.railSheet = false;
    this.realtime = false;
    this.lastTypingSent = {};
    this.listeners = new Set();

    /** Set by Parley Calls when it is installed, so copy that mentions calls can say so. */
    this.callsInstalled = false;
    this.restore();
    this.restoreDrafts();
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  start() {
    this.bindRealtime();
    this.watchIdle();
    this.beat();
    // The heartbeat also carries unread counts, so it runs faster whenever
    // there is no live socket to deliver messages.
    const tick = () => {
      this.beat();
      this.timer = setTimeout(tick, this.socketUp() ? HEARTBEAT : HEARTBEAT_NO_REALTIME);
    };
    this.timer = setTimeout(tick, HEARTBEAT_NO_REALTIME);
    this.poller = setInterval(() => this.pollOpen(), POLL_OPEN);
    this.typingSweep = setInterval(() => this.sweepTyping(), 1000);

    // Reopen whatever was open before the page was reloaded.
    [...this.open, ...this.heads].forEach(id => this.load(id));
    window.addEventListener('resize', () => this.fit());
    // Back online: everything waiting goes now, not at its next retry.
    window.addEventListener('online', () => this.flushQueue(true));
    this.restoreQueue();
    window.addEventListener('pagehide', () => api.leave());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.presenceStale) {
        this.presenceStale = false;
        this.presenceChanged();
      }
    });

    /*
     * 🚨 Moving is noticed by watching, not by Flarum's page hooks. Patching
     * Page.oncreate looked right and never fired when a member clicked into a
     * discussion in Flarum 2, so "Reading · <thread>" only reached others on
     * the next 30-second heartbeat. Once a second is cheap: where() reads the
     * URL and the composer, no request unless the answer changed. Scrolling
     * within a discussion changes nothing here, so it stays quiet.
     */
    this.whereKey = this.placeKey(this.where());
    setInterval(() => {
      const key = this.placeKey(this.where());
      if (key !== this.whereKey) {
        this.whereKey = key;
        this.moved();
      }
    }, 1000);
  }
  get status() {
    if (this.chosenStatus === 'online' && this.idle) return 'away';
    return this.chosenStatus;
  }
  setStatus(status) {
    this.chosenStatus = status;
    this.statusMenu = false;
    this.beat();
    m.redraw();
  }

  /** Where the visitor is right now, in the heartbeat's terms. */
  where() {
    const base = (app.forum.attribute('basePath') || '').replace(/\/$/, '');
    let path = window.location.pathname;
    if (base && path.startsWith(base)) path = path.slice(base.length) || '/';
    const where = placeFromPath(path);
    if (where.tagSlug) {
      const tag = app.store.all('tags').find(t => t.slug() === where.tagSlug);
      where.tagId = tag ? Number(tag.id()) : null;
      delete where.tagSlug;
    }

    // What you are doing, most present first: talking in a room's voice
    // chat, writing a reply, chatting in a room, then the page you are on.
    const voice = app.parleyVoice && app.parleyVoice.session;
    if (voice) return {
      place: 'voice',
      roomId: voice.roomId
    };

    // Writing a reply is its own activity: "Writing a reply · <thread>".
    const c = app.composer;
    const open = c && (typeof c.isVisible === 'function' ? c.isVisible() : c.position && c.position !== 'hidden');
    const composing = open && c.body?.attrs?.discussion;
    if (composing) {
      where.place = 'reply';
      where.discussionId = Number(app.composer.body.attrs.discussion.id());
      return where;
    }
    const room = this.activeRoom();
    if (room) return {
      place: 'room',
      roomId: room
    };
    return where;
  }

  /**
   * The room you are chatting in, if you are: one you opened, typed in or
   * sent to in the last few minutes, still open and not folded away. A room
   * window left idle stops counting, so the line does not claim a room you
   * have drifted away from.
   */
  activeRoom() {
    const use = this.roomUse;
    if (!use || Date.now() - use.at > ROOM_ACTIVE_MS) return null;
    const visible = this.pageConversation === use.id || this.open.includes(use.id) && !this.minimised.has(use.id);
    return visible && this.isRoom(use.id) ? use.id : null;
  }
  touchRoom(id) {
    id = Number(id);
    if (this.isRoom(id)) this.roomUse = {
      id,
      at: Date.now()
    };
  }
  beat() {
    this.lastBeatAt = Date.now();
    return api.heartbeat({
      status: this.status,
      ...this.where()
    }).then(r => {
      if (!r || !r.online) return;
      this.online = r.online;
      this.followingOffline = r.followingOffline || [];
      this.more = r.more || 0;
      this.guests = r.guests || 0;
      this.rooms = r.rooms || [];
      this.requests = r.requests || 0;
      this.quiet = new Set(r.quiet || []);
      this.loaded = true;
      // Keep the open conferences' team lists as fresh as the rest.
      this.expanded.forEach(id => this.loadChildren(id));
      this.applyUnread(r.unread || {});
      m.redraw();
    });
  }

  /** A new place; tell the list now rather than in 30s. */
  moved() {
    clearTimeout(this.movedTimer);
    // A moment's wait, so clicking through three pages sends one update.
    this.movedTimer = setTimeout(() => this.beat(), 800);
  }
  placeKey(w) {
    return [w.place, w.discussionId || '', w.tagId || '', w.roomId || ''].join(':');
  }

  // ── Idle → Away ───────────────────────────────────────────────────────────

  watchIdle() {
    const minutes = this.config.awayMinutes || 10;
    let last = Date.now();
    const active = () => {
      last = Date.now();
      if (this.idle) {
        this.idle = false;
        this.beat();
        m.redraw();
      }
    };
    ['mousemove', 'keydown', 'pointerdown', 'scroll', 'focus'].forEach(e => window.addEventListener(e, active, {
      passive: true
    }));
    setInterval(() => {
      if (!this.idle && Date.now() - last > minutes * 60000) {
        this.idle = true;
        this.beat();
        m.redraw();
      }
    }, 30000);
  }

  // ── Realtime ──────────────────────────────────────────────────────────────

  bindRealtime() {
    if (!this.config.realtime || !('flarum-realtime' in (flarum.extensions || {}))) return;
    let rt;
    try {
      rt = flarum.reg.get('flarum-realtime', 'forum/RealtimeState');
    } catch (e) {
      return;
    }
    if (!rt || typeof rt.onUserChannelReady !== 'function') return;
    this.realtime = true;

    // Re-runs on every reconnect with the new channel object, so bindings are
    // never left on a dead socket.
    // The online list goes live: a nameless "something changed" on the public
    // channel, and this page asks for its own list. Resubscribed on reconnect.
    const subscribePublic = () => {
      try {
        const channel = app.websocket && app.websocket.subscribe('public');
        channel && channel.bind('parley.presence', () => this.presenceChanged());
      } catch (e) {
        // No public channel: the list still refreshes every 30 seconds.
      }
    };
    rt.onUserChannelReady(channel => {
      // Here, not at start: the socket exists once this fires, and it fires
      // again after every reconnect with a fresh socket to subscribe on.
      subscribePublic();
      channel.bind('parley.message', d => this.received(d.message, d.request));
      channel.bind('parley.conversationChanged', d => this.refresh(d.conversationId));
      channel.bind('parley.removed', d => this.forget(d.conversationId));
      channel.bind('parley.messageChanged', d => this.changed(d.message));
      channel.bind('parley.read', d => this.readBy(d));
      channel.bind('parley.typing', d => this.typingFrom(d));
      this.listeners.forEach(fn => fn(channel));
    });
  }

  /**
   * Is the socket actually up? Realtime being installed is not enough: a
   * misconfigured or unreachable daemon leaves the browser "connecting" for
   * ever, and trusting it then means nothing arrives at all. Polling covers
   * every moment the socket is not connected.
   */
  socketUp() {
    if (!this.realtime) return false;
    const state = app.websocket && app.websocket.connection && app.websocket.connection.state;
    return state === 'connected';
  }

  /**
   * Someone arrived, left or changed status. Every open page hears it at
   * once, so each waits a random second or two before asking — a hundred
   * pages should not all ask in the same instant.
   */
  presenceChanged() {
    // 🚨 A tab nobody is looking at does not ask: its own 30-second heartbeat
    // keeps it on the list, and it catches up once when it is shown again.
    // Every open tab of every member hears every signal, so without this one
    // arrival made the whole forum's background tabs heartbeat at once.
    if (document.visibilityState !== 'visible') {
      this.presenceStale = true;
      return;
    }
    if (this.presenceTimer) return;
    // And never more often than once per PRESENCE_MIN_GAP: on a busy forum
    // the signal fires every few seconds, and each one costs a full
    // heartbeat from every visible page.
    const wait = Math.max(0, (this.lastBeatAt || 0) + PRESENCE_MIN_GAP - Date.now());
    this.presenceTimer = setTimeout(() => {
      this.presenceTimer = null;
      this.beat();
    }, wait + 300 + Math.random() * 2000);
  }

  /** For Parley Calls: bind more events on the same channel. */
  onChannel(fn) {
    this.listeners.add(fn);
  }

  // ── Conversations ─────────────────────────────────────────────────────────

  conv(id) {
    return this.convs.get(Number(id));
  }
  remember(messages) {
    (messages || []).forEach(m => m && m.author && this.cards.set(m.author.id, m.author));
  }
  card(userId) {
    return this.cards.get(Number(userId)) || this.person(userId) || null;
  }
  room(id) {
    id = Number(id);
    return this.rooms.find(r => r.id === id) || Object.values(this.childRooms).flat().find(r => r.id === id) || null;
  }
  loadChildren(parentId) {
    return api.rooms(parentId).then(r => {
      if (r && r.rooms) {
        this.childRooms[parentId] = r.rooms;
        m.redraw();
      }
    });
  }
  toggleRoomGroup(parentId) {
    parentId = Number(parentId);
    if (this.expanded.has(parentId)) {
      this.expanded.delete(parentId);
    } else {
      this.expanded.add(parentId);
      if (!this.childRooms[parentId]) this.loadChildren(parentId);
    }
    this.save();
  }
  isRoom(id) {
    const c = this.conv(id);
    return c && c.summary.type === 'room' || !!this.room(id);
  }

  /** Open a room: joining it, so its messages arrive live and it counts unread. */
  openRoom(id) {
    id = Number(id);
    const room = this.room(id);
    const go = () => this.show(id);
    if (room && !room.joined) {
      return api.joinRoom(id).then(r => {
        this.rooms = r.rooms || this.rooms;
        go();
      });
    }
    go();
    return Promise.resolve();
  }
  leaveRoom(id) {
    id = Number(id);
    return api.leaveRoom(id).then(r => {
      this.rooms = r.rooms || this.rooms;
      this.close(id);
    });
  }
  ensure(summary, messages) {
    this.remember(messages);
    const id = summary.id;
    const existing = this.convs.get(id);
    if (existing) {
      existing.summary = summary;
      if (messages) {
        existing.messages = messages;
        existing.loaded = true;
      }
      return existing;
    }
    // A summary from a list carries no messages: `loaded` says whether the
    // conversation itself has been fetched yet.
    const c = {
      summary,
      messages: messages || [],
      loaded: !!messages,
      hasOlder: (messages || []).length >= 40,
      loadingOlder: false,
      typing: {}
    };
    // Where "New messages" goes: after what was read before this visit.
    c.newFrom = summary.lastReadMessageId || 0;
    this.convs.set(id, c);
    return c;
  }

  /** A conversation changed somewhere else — another tab filed or accepted it. */
  refresh(id) {
    if (!this.conv(id)) return;
    api.show(id, {
      latest: 1
    }).then(r => {
      if (!r) return;
      const c = this.conv(id);
      if (c) c.summary = r.conversation;
      this.listChanged();
    });
  }

  /** Gone from a group, or the group is gone. */
  forget(id) {
    id = Number(id);
    this.close(id);
    this.convs.delete(id);
    delete this.unread[id];
    if (this.pageConversation === id) m.route.set(app.route('parley.inbox'));
    this.listChanged();
  }

  /** The inbox page listens for this to redraw its list from the server. */
  listChanged() {
    this.listVersion = (this.listVersion || 0) + 1;
    m.redraw();
  }
  load(id, params) {
    if (params === void 0) {
      params = {};
    }
    return api.show(id, params).then(r => {
      const c = this.ensure(r.conversation, r.messages);
      // Jumping to a search result or a pin replaces what is drawn, so it
      // lands in place; the newest messages are a button away.
      if (params.around) {
        const last = r.messages[r.messages.length - 1];
        c.hasOlder = true;
        c.jumpTo = Number(params.around);
        c.gapAfter = !!last && last.id < (r.conversation.lastMessage?.id || 0);
      } else {
        c.gapAfter = false;
        c.hasOlder = r.messages.length >= 40;
      }
      this.redrawPending(id);
      this.unread[id] = r.conversation.unread;
      this.markRead(id);
      m.redraw();
    }).catch(() => {
      this.close(id);
      // Gone, or never yours: the messages page goes back to the list.
      if (this.pageConversation === Number(id)) m.route.set(app.route('parley.inbox'));
    });
  }

  /** Open the conversation with a person: click their row, their card, anything. */
  openWith(userId) {
    const existing = [...this.convs.values()].find(c => !c.summary.isGroup && c.summary.participants.some(p => p.id === Number(userId)));
    if (existing) return Promise.resolve(this.show(existing.summary.id));
    return api.open(userId).then(r => {
      this.ensure(r.conversation, r.messages);
      this.show(r.conversation.id);
    });
  }

  /** Open a conversation full size, on the inbox page. */
  expand(id) {
    id = Number(id);
    this.close(id);
    m.route.set(app.route('parley.conversation', {
      id
    }));
  }

  /** Open a conversation on the messages page, at a message when one is given. */
  openAt(id, messageId) {
    this.jumpNext = messageId ? {
      id: Number(id),
      around: Number(messageId)
    } : null;
    m.route.set(app.route('parley.conversation', {
      id
    }));
  }

  /** What the messages page does when a conversation is selected. */
  select(id) {
    const jump = this.jumpNext && this.jumpNext.id === Number(id) ? this.jumpNext : null;
    this.jumpNext = null;
    if (jump) return this.load(id, {
      around: jump.around
    });
    if (this.conv(id) && this.conv(id).loaded) return this.markRead(id);
    return this.load(id);
  }
  show(id) {
    id = Number(id);
    // On the inbox page the conversation opens there, not in a window over it.
    if (this.onInbox()) {
      m.route.set(app.route('parley.conversation', {
        id
      }));
      return;
    }
    this.heads = this.heads.filter(h => h !== id);
    this.minimised.delete(id);
    if (!this.open.includes(id)) {
      this.open.push(id);
    }
    this.fit();
    this.railSheet = false;
    this.touchRoom(id);
    this.markRead(id);
    this.save();
    m.redraw();
    if (!this.conv(id) || !this.conv(id).loaded) this.load(id);
    setTimeout(() => document.getElementById(`Parley-input-${id}`)?.focus(), 0);
  }

  /** Keep only as many windows as fit; the oldest fall back to heads. */
  fit() {
    const width = window.innerWidth;
    if (width < MOBILE_BREAKPOINT) {
      while (this.open.length > 1) this.heads.unshift(this.open.shift());
      return;
    }
    const rail = this.railDocked() ? RAIL_WIDTH : 0;
    const max = windowsThatFit(width, rail);
    while (this.open.length > max) this.heads.unshift(this.open.shift());
    this.heads = this.heads.slice(0, MAX_HEADS);
  }
  toHead(id) {
    id = Number(id);
    this.open = this.open.filter(o => o !== id);
    if (!this.heads.includes(id)) this.heads.unshift(id);
    this.heads = this.heads.slice(0, MAX_HEADS);
    this.save();
  }
  toggleMinimised(id) {
    id = Number(id);
    this.minimised.has(id) ? this.minimised.delete(id) : this.minimised.add(id);
    if (!this.minimised.has(id)) this.markRead(id);
    this.save();
  }
  close(id) {
    id = Number(id);
    this.open = this.open.filter(o => o !== id);
    this.heads = this.heads.filter(h => h !== id);
    this.minimised.delete(id);
    delete this.replyTo[id];
    delete this.editing[id];
    this.save();
    m.redraw();
  }
  isVisible(id) {
    id = Number(id);
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') return false;
    // Open in the inbox page counts as being read, the same as a docked window.
    if (this.pageConversation === id) return true;
    return this.open.includes(id) && !this.minimised.has(id);
  }
  older(id) {
    const c = this.conv(id);
    if (!c || c.loadingOlder || !c.hasOlder || !c.messages.length) return;
    c.loadingOlder = true;
    return api.show(id, {
      before: c.messages[0].id
    }).then(r => {
      c.messages = [...r.messages, ...c.messages];
      c.hasOlder = r.messages.length >= 40;
      c.loadingOlder = false;
      m.redraw();
    });
  }

  // ── Messages in ───────────────────────────────────────────────────────────

  received(message, request) {
    if (request === void 0) {
      request = false;
    }
    this.remember([message]);
    const id = message.conversationId;
    const c = this.conv(id);
    if (!c) {
      // A conversation this tab has not seen yet: fetch it, then show it.
      api.show(id).then(r => {
        this.ensure(r.conversation, r.messages);
        this.arrived(id, message, request || r.conversation.request);
        this.listChanged();
      });
      return;
    }

    // Our own message coming back: it replaces the one drawn while sending.
    if (message.nonce) {
      c.messages = c.messages.filter(x => x.nonce !== message.nonce || !x.pending);
      this.queue = this.queue.filter(q => q.nonce !== message.nonce);
    }
    if (!c.messages.some(x => x.id === message.id)) {
      c.messages.push(message);
      c.messages.sort((a, b) => (a.pending ? 1 : 0) - (b.pending ? 1 : 0) || a.id - b.id);
    }
    if (request) c.summary.request = true;
    c.summary.lastMessage = message;
    c.summary.lastMessageAt = message.createdAt;
    if (message.userId) delete c.typing[message.userId];
    this.arrived(id, message, request || c.summary.request);
    this.listChanged();
  }
  arrived(id, message, request) {
    if (request === void 0) {
      request = false;
    }
    const mine = message.userId === Number(this.me.id());

    // A request is filed quietly: no head, no flash, no count on the badge.
    if (request && !mine) {
      if (!this.isVisible(id)) this.requests = Math.max(this.requests, 1);
      m.redraw();
      return;
    }
    if (!mine) {
      if (this.isVisible(id)) {
        this.markRead(id);
      } else {
        this.unread[id] = (this.unread[id] || 0) + 1;
        // Messenger brings the conversation forward as a head. Busy keeps it
        // quiet: the badge still counts, nothing pops. A room never pops: it
        // counts in the list, it does not interrupt.
        if (!this.isRoom(id) && !this.isMuted(id) && !this.onInbox() && !this.open.includes(id) && !this.heads.includes(id) && this.status !== 'busy') {
          this.heads.unshift(id);
          this.heads = this.heads.slice(0, MAX_HEADS);
          this.save();
        }
        this.flash.add(id);
        setTimeout(() => {
          this.flash.delete(id);
          m.redraw();
        }, 1600);
      }
    }
    m.redraw();
  }
  changed(message) {
    const c = this.conv(message.conversationId);
    if (!c) return;
    const i = c.messages.findIndex(x => x.id === message.id);
    if (i >= 0) c.messages[i] = message;
    m.redraw();
  }
  readBy(_ref) {
    let {
      conversationId,
      userId,
      messageId
    } = _ref;
    const c = this.conv(conversationId);
    if (!c) return;
    if (userId === Number(this.me.id())) {
      // Read in another tab.
      this.unread[conversationId] = 0;
    } else {
      c.summary.seenBy = {
        ...(c.summary.seenBy || {}),
        [userId]: messageId
      };
    }
    m.redraw();
  }
  typingFrom(_ref2) {
    let {
      conversationId,
      userId
    } = _ref2;
    const c = this.conv(conversationId);
    if (!c) return;
    c.typing[userId] = Date.now() + TYPING_TTL;
    m.redraw();
  }
  sweepTyping() {
    let changed = false;
    const now = Date.now();
    this.convs.forEach(c => {
      for (const uid in c.typing) {
        if (c.typing[uid] < now) {
          delete c.typing[uid];
          changed = true;
        }
      }
    });
    if (changed) m.redraw();
  }
  applyUnread(map) {
    for (const key in map) {
      const id = Number(key);
      const n = map[key];
      if (n > 0 && !this.isRoom(id) && !this.quiet.has(id) && !this.onInbox() && !this.isVisible(id) && !this.open.includes(id) && !this.heads.includes(id) && this.status !== 'busy') {
        this.heads.push(id);
        if (!this.conv(id)) this.load(id);
      }
    }
    this.unread = {
      ...map
    };
    // A room counts in the list only, once you have joined it.
    this.rooms.forEach(r => {
      if (map[r.id] !== undefined) r.unread = map[r.id];
    });
    // Anything open and in view is being read, whatever the server counted.
    [...this.open, this.pageConversation].forEach(id => id && this.isVisible(id) && (this.unread[id] = 0));
    this.heads = this.heads.slice(0, MAX_HEADS);
  }

  /**
   * Without realtime: refresh the open conversations.
   *
   * The latest page, not just what is newer than the last id. "Newer than"
   * finds new messages but never sees a reaction, an edit or a read receipt
   * on one already drawn, so without realtime those would never arrive.
   */
  pollOpen() {
    if (this.socketUp() || document.visibilityState !== 'visible') return;
    this.open.forEach(id => {
      const c = this.conv(id);
      if (!c || this.minimised.has(id)) return;
      api.show(id, {
        latest: 1
      }).then(r => {
        if (!r) return;
        c.summary = {
          ...r.conversation,
          lastReadMessageId: Math.max(r.conversation.lastReadMessageId || 0, c.summary.lastReadMessageId || 0)
        };
        r.messages.forEach(msg => {
          const i = c.messages.findIndex(x => x.id === msg.id);
          if (i >= 0) c.messages[i] = msg;else this.received(msg, r.conversation.request);
        });
        m.redraw();
      });
    });
  }

  /**
   * Everyone online, the way a person counts the room: you included (unless
   * you appear offline), and the ones past the list's limit. The server leaves
   * you out of your own list, so the list's length alone reads "0 online"
   * while you are sitting there.
   */
  onlineCount() {
    return this.online.length + (this.more || 0) + (this.status === 'invisible' ? 0 : 1);
  }

  /** Direct messages only: a busy room should not shout from the pill, nor a muted chat. */
  totalUnread() {
    return Object.entries(this.unread).reduce((a, _ref3) => {
      let [id, n] = _ref3;
      return a + (this.isRoom(id) || this.isMuted(id) ? 0 : n || 0);
    }, 0);
  }

  /** Muted or archived: news that neither pops up nor counts on the badge. */
  isMuted(id) {
    id = Number(id);
    const c = this.conv(id);
    return c ? !!(c.summary.muted || c.summary.archived) : this.quiet.has(id);
  }
  onInbox() {
    return !!(app.current && app.current.get && app.current.get('routeName') && String(app.current.get('routeName')).startsWith('parley.'));
  }

  // ── Messages out ──────────────────────────────────────────────────────────

  /**
   * Send a message. It is drawn at once, greyed, and goes through the queue:
   * a dropped connection retries it with the same nonce (the server answers
   * a repeat with the message it already made), and a refusal leaves it
   * marked, with Retry and Delete, instead of losing the words.
   */
  send(id, body) {
    id = Number(id);
    this.touchRoom(id);
    const replyTo = this.replyTo[id];
    delete this.replyTo[id];
    this.setDraft(id, '');
    const item = {
      nonce: nonce(),
      conversationId: id,
      body,
      replyToId: replyTo ? replyTo.id : null,
      tries: 0,
      state: 'sending'
    };
    this.queue.push(item);
    this.drawPending(item, replyTo);
    this.saveQueue();
    return this.attempt(item);
  }

  /** The greyed bubble of a message on its way. */
  drawPending(item, replyTo) {
    if (replyTo === void 0) {
      replyTo = null;
    }
    const c = this.conv(item.conversationId);
    if (!c || c.messages.some(x => x.nonce === item.nonce)) return;
    c.messages.push({
      id: Number.MAX_SAFE_INTEGER - this.queue.indexOf(item),
      nonce: item.nonce,
      pending: true,
      failed: item.state === 'failed',
      conversationId: item.conversationId,
      userId: Number(this.me.id()),
      type: 'text',
      body: item.body,
      replyTo: replyTo ? {
        id: replyTo.id,
        excerpt: replyTo.body,
        type: replyTo.type
      } : null,
      reactions: [],
      createdAt: new Date().toISOString()
    });
    m.redraw();
  }
  attempt(item) {
    item.state = 'sending';
    this.markPending(item);
    return api.send(item.conversationId, item.body, item.replyToId, item.nonce).then(r => {
      this.queue = this.queue.filter(q => q !== item);
      this.saveQueue();
      this.received(r.message);
    }, error => {
      // No answer, or the server was having a moment: try again. Anything
      // else is the server saying no, which trying again will not change.
      const transient = !error || !error.status || error.status >= 500 || error.status === 429;
      item.tries++;
      if (transient) {
        item.state = 'waiting';
        clearTimeout(item.timer);
        item.timer = setTimeout(() => this.attempt(item), RETRY_STEPS[Math.min(item.tries - 1, RETRY_STEPS.length - 1)]);
      } else {
        item.state = 'failed';
        item.error = error.alert && error.alert.content;
      }
      this.markPending(item);
      this.saveQueue();
    });
  }
  markPending(item) {
    const c = this.conv(item.conversationId);
    const msg = c && c.messages.find(x => x.nonce === item.nonce && x.pending);
    if (msg) {
      msg.failed = item.state === 'failed';
      msg.waiting = item.state === 'waiting';
      msg.error = item.error || null;
    }
    m.redraw();
  }
  retry(nonceValue) {
    const item = this.queue.find(q => q.nonce === nonceValue);
    if (item) this.attempt(item);
  }
  discard(nonceValue) {
    const item = this.queue.find(q => q.nonce === nonceValue);
    if (item) clearTimeout(item.timer);
    this.queue = this.queue.filter(q => q.nonce !== nonceValue);
    this.convs.forEach(c => c.messages = c.messages.filter(x => x.nonce !== nonceValue || !x.pending));
    this.saveQueue();
    m.redraw();
  }
  flushQueue(now) {
    this.queue.filter(q => q.state === 'waiting').forEach(q => {
      clearTimeout(q.timer);
      if (now) this.attempt(q);
    });
  }
  saveQueue() {
    try {
      const keep = this.queue.map(_ref4 => {
        let {
          nonce,
          conversationId,
          body,
          replyToId,
          state
        } = _ref4;
        return {
          nonce,
          conversationId,
          body,
          replyToId,
          state
        };
      });
      localStorage.setItem(this.key() + ':queue', JSON.stringify(keep));
    } catch (e) {
      // No storage: the queue still works for this page.
    }
  }

  /** Messages still on their way when the page was closed go on their way again. */
  restoreQueue() {
    let saved = [];
    try {
      saved = JSON.parse(localStorage.getItem(this.key() + ':queue') || '[]');
    } catch (e) {
      // Nothing saved.
    }
    saved.forEach(q => {
      const item = {
        ...q,
        tries: 0,
        state: q.state === 'failed' ? 'failed' : 'waiting'
      };
      this.queue.push(item);
      const draw = () => this.drawPending(item);
      this.conv(item.conversationId) ? draw() : this.load(item.conversationId).then(draw);
      if (item.state !== 'failed') this.attempt(item);
    });
  }

  /** Pending messages drawn again after a conversation is fetched afresh. */
  redrawPending(id) {
    this.queue.filter(q => q.conversationId === Number(id)).forEach(q => this.drawPending(q));
  }

  /**
   * A picture, video, voice note or file. Not queued — a file cannot be kept
   * across a reload — but it reports its progress on the conversation.
   */
  upload(id, file, kind, extra) {
    if (extra === void 0) {
      extra = {};
    }
    const c = this.conv(id);
    if (c) c.uploading = {
      name: file.name || '',
      progress: 0
    };
    m.redraw();
    const done = () => {
      if (c) c.uploading = null;
      m.redraw();
    };
    return api.upload(id, file, kind, {
      ...extra,
      nonce: nonce()
    }, p => {
      if (c && c.uploading) {
        c.uploading.progress = p;
        m.redraw();
      }
    }).then(r => {
      done();
      this.received(r.message);
    }, error => {
      done();
      app.alerts.show({
        type: 'error'
      }, error && error.alert && error.alert.content || app.translator.trans('core.lib.error.generic_message'));
    });
  }

  // ── Drafts ────────────────────────────────────────────────────────────────

  setDraft(id, text) {
    id = Number(id);
    if (text && text.trim()) this.drafts[id] = text;else delete this.drafts[id];
    clearTimeout(this.draftTimer);
    this.draftTimer = setTimeout(() => {
      try {
        localStorage.setItem(this.key() + ':drafts', JSON.stringify(this.drafts));
      } catch (e) {
        // Drafts are a convenience; without storage they last the page.
      }
    }, 400);
  }
  restoreDrafts() {
    try {
      this.drafts = JSON.parse(localStorage.getItem(this.key() + ':drafts') || '{}') || {};
    } catch (e) {
      this.drafts = {};
    }
  }

  // ── Filing ────────────────────────────────────────────────────────────────

  /** Mute, archive, star or label, drawn at once and saved behind. */
  file(id, changes) {
    const c = this.conv(id);
    if (c) {
      if ('muted' in changes) c.summary.muted = changes.muted;
      if ('archived' in changes) c.summary.archived = changes.archived;
      if ('starred' in changes) c.summary.starred = changes.starred;
      if ('label' in changes) c.summary.label = changes.label;
    }
    if ('muted' in changes) changes.muted ? this.quiet.add(Number(id)) : this.quiet.delete(Number(id));
    m.redraw();
    return api.settings(id, changes).then(() => this.listChanged());
  }
  accept(id) {
    return api.accept(id).then(() => {
      const c = this.conv(id);
      if (c) c.summary.request = false;
      this.requests = Math.max(0, this.requests - 1);
      this.listChanged();
      this.beat();
    });
  }

  /** Pin or unpin a message for everyone in the conversation. */
  pin(messageId, on) {
    return api.pin(messageId, on).then(r => {
      this.changed(r.message);
      const c = this.conv(r.message.conversationId);
      if (c) {
        c.summary.pinCount = Math.max(0, (c.summary.pinCount || 0) + (on ? 1 : -1));
        c.pins = null;
      }
    });
  }
  forward(messageId, conversationIds) {
    return api.forward(messageId, conversationIds).then(r => {
      r.messages.forEach(msg => this.received(msg));
      return r.messages;
    });
  }

  /** A new group: drawn and opened as soon as the server has it. */
  createGroup(userIds, name) {
    return api.createGroup(userIds, name).then(r => {
      this.ensure(r.conversation, r.messages);
      this.listChanged();
      this.show(r.conversation.id);
      return r.conversation;
    });
  }

  /** After a group change: the new summary and member list. */
  groupChanged(r) {
    if (!r || r.left) return;
    const c = this.conv(r.conversation.id);
    if (c) c.summary = {
      ...c.summary,
      ...r.conversation
    };
    if (r.members) this.members[r.conversation.id] = r.members;
    this.listChanged();
  }
  leaveGroup(id) {
    return api.removeMember(id, this.me.id()).then(() => this.forget(id));
  }
  edit(messageId, body) {
    return api.edit(messageId, body).then(r => this.changed(r.message));
  }
  remove(messageId) {
    return api.remove(messageId).then(r => this.changed(r.message));
  }
  react(messageId, emoji) {
    return api.react(messageId, emoji).then(r => this.changed(r.message));
  }
  typing(id) {
    this.touchRoom(id);
    const now = Date.now();
    if (now - (this.lastTypingSent[id] || 0) < TYPING_SEND_EVERY) return;
    this.lastTypingSent[id] = now;
    if (this.socketUp()) api.action(id, 'typing');
  }
  markRead(id) {
    const c = this.conv(id);
    const sent = c ? c.messages.filter(x => !x.pending) : [];
    if (!c || !sent.length || !this.isVisible(id)) return;
    const last = sent[sent.length - 1].id;
    this.unread[id] = 0;
    if ((c.summary.lastReadMessageId || 0) >= last) return;
    c.summary.lastReadMessageId = last;
    api.action(id, 'read', {
      messageId: last
    });
  }
  hide(id) {
    api.action(id, 'hide');
    this.close(id);
    this.listChanged();
  }

  // ── People ────────────────────────────────────────────────────────────────

  search(q) {
    this.filter = q;
    clearTimeout(this.searchTimer);
    if (q.trim().length < 2) {
      this.found = [];
      return;
    }
    this.searchTimer = setTimeout(() => {
      api.people(q.trim()).then(r => {
        if (this.filter === q) {
          this.found = r.people || [];
          m.redraw();
        }
      });
    }, 250);
  }
  follow(userId, on) {
    return api.person(userId, 'follow', on).then(() => this.beat());
  }
  block(userId, on) {
    return api.person(userId, 'block', on).then(() => {
      this.beat();
      [...this.convs.values()].filter(c => !c.summary.isGroup && c.summary.participants.some(p => p.id === Number(userId))).forEach(c => this.close(c.summary.id));
    });
  }
  person(userId) {
    userId = Number(userId);
    return this.online.find(p => p.id === userId) || this.followingOffline.find(p => p.id === userId) || [...this.convs.values()].flatMap(c => c.summary.participants).find(p => p.id === userId);
  }

  /** Live presence for someone in a conversation, from the latest heartbeat. */
  presenceOf(userId) {
    const p = this.online.find(x => x.id === Number(userId));
    if (p) return p;
    const f = this.followingOffline.find(x => x.id === Number(userId));
    return f || {
      status: 'offline',
      activity: null
    };
  }

  // ── Layout ────────────────────────────────────────────────────────────────

  isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }
  railDocked() {
    return this.railPinned && window.innerWidth >= DOCK_BREAKPOINT;
  }

  /** The online button and the list's close button: open or close the panel; a docked list floats again. */
  toggleRail() {
    if (this.railDocked()) {
      this.pinRail(false);
      return;
    }
    this.railSheet = !this.railSheet;
  }

  /** Pin the list down the side of the page, or let it float again. */
  pinRail(on) {
    this.railPinned = on;
    this.railHidden = !on;
    this.railSheet = false;
    this.fit();
    this.save();
    m.redraw();
  }

  // ── Persistence ───────────────────────────────────────────────────────────

  key() {
    return 'parley:ui:' + (this.me ? this.me.id() : 'guest');
  }
  save() {
    try {
      localStorage.setItem(this.key(), JSON.stringify({
        open: this.open,
        heads: this.heads,
        min: [...this.minimised],
        pinned: this.railPinned,
        expanded: [...this.expanded]
      }));
    } catch (e) {
      // Private windows refuse storage; Parley works without it.
    }
  }
  restore() {
    try {
      const s = JSON.parse(localStorage.getItem(this.key()) || '{}');
      this.open = (s.open || []).map(Number);
      this.heads = (s.heads || []).map(Number);
      this.minimised = new Set((s.min || []).map(Number));
      // Floating is the default; only a member who pinned it gets it docked.
      this.railPinned = !!s.pinned;
      this.railHidden = !this.railPinned;
      this.expanded = new Set((s.expanded || []).map(Number));
    } catch (e) {
      // Nothing saved, or storage refused.
    }
  }
}

/** A random id for one send, so a retry is recognised as the same message. */
function nonce() {
  const bytes = new Uint8Array(12);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}